import {
  faCheck,
  faClock,
  faCommentSms
} from '@fortawesome/free-solid-svg-icons'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import { useNewMessageMutation } from '~/api/mutations/action'
import { useMessageSendingInfo } from '~/api/queries/global'
import { EditorInput } from '~/components/Form'
import { DateInput } from '~/components/Form/Input/DateInput'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import { Button } from '~/components/Global/Interactions/Button/Button'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import {
  ButtonTabGroup,
  type ButtonTabOption
} from '~/components/Global/Interactions/ButtonTabGroup/ButtonTabGroup'
import { Dropdown } from '~/components/Global/Interactions/Dropdown/Dropdown'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { Toggle } from '~/components/Global/Interactions/Toggle/Toggle'
import { useUpdateApplicantAvailability } from '~/domains/applicants/mutations/updateApplicantAvailability'
import { useConfig } from '~/domains/configuration/queries/config'
import { SmsAlerts } from '~/domains/messaging/components/SmsAlerts'
import { SmsInfoBar } from '~/domains/messaging/components/SmsInfoBar'
import { useSmsPreview } from '~/domains/messaging/queries/useSmsPreview'
import { useActiveAgency } from '~/domains/users/hooks/useActiveAgency'
import { toast } from '~/utils'
import { toISOLocal } from '~/utils/date'
import { captureApplicantAvailability } from '../../analytics'
import { useApplicantHead } from '../../queries/applicantHead'
import {
  availabilityDropdownTitle,
  availabilityDropdownWrapper,
  availabilityInputGroup
} from './Availability.css'

type AvailabilityMode = 'immediate' | 'planned' | 'sms'

const modeOptions: ButtonTabOption<AvailabilityMode>[] = [
  {
    label: 'Immédiate',
    value: 'immediate',
    icon: faCheck,
    variant: 'success'
  },
  { label: 'Prévue', value: 'planned', icon: faClock },
  { label: 'Demande par SMS', value: 'sms', icon: faCommentSms }
]

export type AvailabilityDropdownProps = {
  applicantId: number
  isOpen: boolean
  onClose: () => void
  dropdownRef: React.RefObject<HTMLDivElement | HTMLButtonElement>
  placement: 'bottom-end' | 'bottom-start'
  onUpdateAvailability?: (applicantId: number) => void
  isValidPhoneMobile?: boolean
  acceptsMessaging?: boolean
  source?: string
}

export const AvailabilityDropdown = ({
  applicantId,
  isOpen,
  onClose,
  dropdownRef,
  placement,
  onUpdateAvailability,
  isValidPhoneMobile,
  acceptsMessaging,
  source = 'unknown'
}: AvailabilityDropdownProps) => {
  const posthog = usePostHog()
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date())
  const [createUnavailability, setCreateUnavailability] = useState(false)
  const [selectedMode, setSelectedMode] =
    useState<AvailabilityMode>('immediate')
  const [smsContent, setSmsContent] = useState('')

  const {
    mutate: updateAvailability,
    isPending,
    isSuccess: isUpdated
  } = useUpdateApplicantAvailability(applicantId)

  const { data: smsEnabled } = useConfig('sms.template_availability_enabled', {
    enabled: isOpen
  })
  const { activeAgency } = useActiveAgency()
  const effectiveAgencyId = activeAgency?.id ?? null
  const { data: sendingInfo, isLoading: isLoadingSendingInfo } =
    useMessageSendingInfo({
      agencyId: effectiveAgencyId,
      enabled: isOpen
    })
  const { data: applicant } = useApplicantHead({
    applicantId:
      isValidPhoneMobile === undefined || acceptsMessaging === undefined
        ? applicantId
        : undefined,
    additionalFields: ['phoneMobile', 'acceptsMessaging']
  })
  const { data: smsPreview, isLoading: isLoadingPreview } = useSmsPreview(
    selectedMode === 'sms',
    `/v1/applicants/${applicantId}`,
    effectiveAgencyId ? `/v1/agencies/${effectiveAgencyId}` : undefined,
    'availability'
  )

  useEffect(() => {
    if (smsPreview?.content) {
      setSmsContent(smsPreview.content)
    }
  }, [smsPreview?.content])

  const {
    mutate: sendAvailabilitySms,
    isPending: isSendingAvailabilitySms,
    isSuccess: isSmsSent
  } = useNewMessageMutation()

  const isInvalidPhoneMobile =
    isValidPhoneMobile === false ||
    (isValidPhoneMobile === undefined &&
      !applicant?.phoneMobile?.match(/^\+?(0|33)[67]/))

  const canSend =
    activeAgency !== null &&
    (isLoadingSendingInfo || (sendingInfo?.smsRemaining ?? 0) > 0)

  const handleSubmit = () => {
    const availabilityDate = selectedDate || new Date()
    const formattedDate = toISOLocal(availabilityDate).slice(0, 10)

    updateAvailability(
      {
        date: formattedDate,
        createUnavailability: createUnavailability && selectedMode === 'planned'
      },
      {
        onSuccess: () => {
          setSelectedDate(new Date())
          setCreateUnavailability(false)
          setSelectedMode('immediate')
          onClose()
          onUpdateAvailability?.(applicantId)
          captureApplicantAvailability(posthog, 'validate', {
            applicantId,
            source,
            mode: selectedMode,
            type: 'update'
          })
        }
      }
    )
  }

  const handleSendSms = () => {
    sendAvailabilitySms(
      {
        applicants: [applicantId],
        availabilityRequest: true,
        message: smsContent || undefined
      },
      {
        onSuccess: () => {
          toast('SMS envoyé !', '', ToastType.Success)
          onClose()
          captureApplicantAvailability(posthog, 'validate', {
            applicantId,
            source,
            mode: selectedMode,
            type: 'sms'
          })
        },
        onError: (error) => {
          toast(
            "Le SMS n'a pas pu être envoyé",
            error.response.message,
            ToastType.Danger
          )
        }
      }
    )
  }

  return (
    <Dropdown
      isOpen={isOpen}
      onclose={onClose}
      dropdownRef={dropdownRef}
      placement={placement}
      className={availabilityDropdownWrapper}
    >
      <div className={availabilityDropdownTitle}>
        Mise à jour de la disponibilité
      </div>

      <ButtonTabGroup
        options={modeOptions.filter((o) => smsEnabled || o.value !== 'sms')}
        value={selectedMode}
        onChange={(mode) => {
          captureApplicantAvailability(posthog, 'tab', {
            applicantId,
            source,
            tab: mode
          })
          setSelectedMode(mode)
          setCreateUnavailability(false)
          setSelectedDate(new Date())
        }}
      />

      {selectedMode === 'planned' && (
        <div className={availabilityInputGroup}>
          <DateInput
            showTime={false}
            noPast={false}
            popperPlacement="left-end"
            label="Date de disponibilité"
            defaultDate={selectedDate}
            onChange={setSelectedDate}
            wrapper={false}
          />
        </div>
      )}

      {selectedMode === 'planned' && (
        <Toggle
          key={selectedMode}
          defaultState={createUnavailability}
          onChange={setCreateUnavailability}
          label="Créer une indisponibilité jusqu'à cette date"
        />
      )}

      {selectedMode !== 'sms' && (
        <ButtonGroup align="right">
          <Button
            onClick={() => {
              onClose()
            }}
            disabled={isPending}
            variation={ButtonVariation.Neutral}
          >
            {isUpdated ? 'Fermer' : 'Annuler'}
          </Button>
          <Button
            onClick={() => {
              handleSubmit()
            }}
            disabled={isPending}
            variation={ButtonVariation.Primary}
          >
            {isPending ? 'En cours...' : 'Valider'}
          </Button>
        </ButtonGroup>
      )}

      {selectedMode === 'sms' && smsEnabled && (
        <>
          <SmsAlerts
            sendingInfo={sendingInfo}
            isLoading={isLoadingSendingInfo}
            isInvalidPhone={isInvalidPhoneMobile}
            acceptsMessaging={acceptsMessaging ?? applicant?.acceptsMessaging}
          />
          {isSmsSent && (
            <Alert
              message="SMS envoyé !"
              variation={AlertVariation.Success}
              onDismiss={false}
            />
          )}

          {!isSmsSent &&
            canSend &&
            !isInvalidPhoneMobile &&
            (acceptsMessaging || applicant?.acceptsMessaging) && (
              <>
                {(isLoadingSendingInfo || isLoadingPreview) && (
                  <Shimmer height={120} width={425} />
                )}

                {smsPreview?.content && (
                  <>
                    <EditorInput
                      content={smsContent}
                      textOnly
                      onChange={(value) => setSmsContent(value)}
                      autofocus={false}
                      showBottomMenu={false}
                      placeholderCategories={['Identité']}
                    />
                    {sendingInfo && (
                      <SmsInfoBar
                        content={smsContent ?? smsPreview?.content}
                        sendingInfo={sendingInfo}
                      />
                    )}
                  </>
                )}

                <ButtonGroup align="right">
                  <Button
                    onClick={() => {
                      onClose()
                    }}
                    disabled={isSendingAvailabilitySms}
                    variation={ButtonVariation.Neutral}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={() => {
                      handleSendSms()
                    }}
                    disabled={isSendingAvailabilitySms}
                    variation={ButtonVariation.Primary}
                  >
                    {isSendingAvailabilitySms ? 'Envoi...' : 'Envoyer le SMS'}
                  </Button>
                </ButtonGroup>
              </>
            )}
        </>
      )}
    </Dropdown>
  )
}
